/**
 * LandingPage - Public landing page with customizable hero and sections
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { eventAPI } from '@/lib/api';
import api from '@/lib/api';
import { PublicLayout } from '@/components/layouts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Calendar, Users, QrCode, Ticket, Coins, Shield, MessageCircle,
  MapPin, ArrowRight, ChefHat, CreditCard, Building2, Star, Quote,
  Clock, Mail, Phone, Globe, Video, AlertTriangle
} from 'lucide-react';

// Icon mapping
const IconMap = {
  Calendar, Users, QrCode, Ticket, Coins, Shield, MessageCircle,
  MapPin, ArrowRight, ChefHat, CreditCard, Building2, Star, Clock,
  Mail, Phone, Globe, Video, Quote
};

// Helper to get proper image URL
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.includes('test.com') || url.includes('example.com') || url.includes('placeholder')) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
};

// Event Card Component
const EventCard = ({ event }) => {
  const navigate = useNavigate();
  const primaryColor = event.branding?.primary_color || "#3B82F6";
  const logoUrl = getImageUrl(event.branding?.logo_url);

  return (
    <Card 
      className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" 
      onClick={() => navigate(`/event/${event.slug || event.id}`)}
      data-testid={`event-card-${event.id}`}
    >
      <div className="h-2" style={{ backgroundColor: primaryColor }} />
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {logoUrl && (
            <img src={logoUrl} alt="" className="h-8 w-8 object-contain" />
          )}
          {event.name}
        </CardTitle>
        <CardDescription>
          {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 line-clamp-2">{event.description}</p>
        {event.venue?.name && (
          <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
            <MapPin className="h-4 w-4" />
            {event.venue.name}
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button className="w-full" style={{ backgroundColor: primaryColor }}>
          Register Now <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

// Hero Section Component
const HeroSection = ({ config }) => {
  const navigate = useNavigate();
  
  const heroType = config?.hero_type || 'gradient';
  const gradientFrom = config?.hero_gradient_from || '#3B82F6';
  const gradientTo = config?.hero_gradient_to || '#6366F1';
  const solidColor = config?.hero_color || '#3B82F6';
  const imageUrl = getImageUrl(config?.hero_image_url);
  const videoUrl = getImageUrl(config?.hero_video_url);
  const overlayOpacity = config?.hero_overlay_opacity ?? 20;
  const title = config?.hero_title || config?.app_name || 'EventFlow';
  const subtitle = config?.hero_subtitle || 'AI-Native Event Management Platform';
  const textColor = config?.hero_text_color || '#FFFFFF';
  const ctaText = config?.hero_cta_text || 'Get Started';
  const ctaLink = config?.hero_cta_link || '/login';

  const getBackgroundStyle = () => {
    switch (heroType) {
      case 'gradient':
        return { background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})` };
      case 'color':
        return { backgroundColor: solidColor };
      case 'image':
        return imageUrl 
          ? { backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})` };
      case 'video':
        return { backgroundColor: '#000' };
      default:
        return { background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})` };
    }
  };

  return (
    <div className="relative overflow-hidden" style={getBackgroundStyle()}>
      {heroType === 'video' && videoUrl && (
        <video 
          src={videoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay muted loop playsInline
        />
      )}
      
      <div 
        className="absolute inset-0"
        style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity / 100})` }}
      />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            style={{ color: textColor }}
          >
            {title}
          </h1>
          <p 
            className="mt-4 text-xl sm:text-2xl max-w-2xl mx-auto"
            style={{ color: textColor, opacity: 0.9 }}
          >
            {subtitle}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate(ctaLink)}
              data-testid="hero-cta-btn"
            >
              {ctaText}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white/10 border-white/30 hover:bg-white/20"
              style={{ color: textColor }}
              onClick={() => navigate("/admin/register")}
              data-testid="admin-register-btn"
            >
              Admin Registration
            </Button>
          </div>
          
          {/* Staff Login Links */}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button
              size="sm"
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate("/security/login")}
            >
              <Shield className="h-4 w-4 mr-1" />
              Security
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate("/dining/login")}
            >
              <ChefHat className="h-4 w-4 mr-1" />
              Kitchen/POS
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate("/housing/login")}
            >
              <Building2 className="h-4 w-4 mr-1" />
              Housing
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Features Section Component
const FeaturesSection = ({ config }) => {
  if (!config?.features_enabled) return null;
  
  const features = config?.features || [];
  const title = config?.features_title || 'Complete Event Lifecycle Management';

  return (
    <div className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-slate-900 text-center mb-12">
          {title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, i) => {
            const Icon = IconMap[feature.icon] || Star;
            return (
              <div key={i} className="text-center p-6 rounded-xl hover:bg-slate-50 transition-colors">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 mb-4">
                  <Icon className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900 text-lg">{feature.title}</h3>
                <p className="text-sm text-slate-600 mt-2">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Testimonials Section Component
const TestimonialsSection = ({ config }) => {
  if (!config?.testimonials_enabled || !config?.testimonials?.length) return null;
  
  const testimonials = config.testimonials;
  const title = config?.testimonials_title || 'What Our Clients Say';

  return (
    <div className="bg-slate-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-slate-900 text-center mb-12">
          {title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <Card key={i} className="bg-white border-0 shadow-sm">
              <CardContent className="pt-6">
                <Quote className="h-8 w-8 text-blue-200 mb-4" />
                <p className="text-slate-600 italic mb-4">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={testimonial.avatar} />
                    <AvatarFallback className="bg-blue-100 text-blue-600">
                      {testimonial.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Partners Section Component
const PartnersSection = ({ config }) => {
  if (!config?.partners_enabled || !config?.partners?.length) return null;
  
  const partners = config.partners;
  const title = config?.partners_title || 'Trusted By';

  return (
    <div className="bg-white py-12 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-lg font-medium text-slate-500 text-center mb-8">
          {title}
        </h2>
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
          {partners.map((partner, i) => (
            <div key={i} className="grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100">
              {partner.logo ? (
                <img 
                  src={partner.logo} 
                  alt={partner.name} 
                  className="h-10 w-auto object-contain"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                />
              ) : null}
              <span className={partner.logo ? "hidden" : "text-slate-600 font-medium"}>{partner.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// FAQ Section Component
const FAQSection = ({ config }) => {
  if (!config?.faq_enabled || !config?.faq_items?.length) return null;
  
  const faqItems = config.faq_items;
  const title = config?.faq_title || 'Frequently Asked Questions';

  return (
    <div className="bg-slate-50 py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
          {title}
        </h2>
        <Accordion type="single" collapsible className="w-full">
          {faqItems.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="bg-white mb-2 rounded-lg border-0 shadow-sm">
              <AccordionTrigger className="px-6 py-4 hover:no-underline text-left font-medium">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-600">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  
  // Fetch events
  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ["events"],
    queryFn: () => eventAPI.list().then((r) => r.data),
  });

  // Fetch app config (all landing page settings)
  const { data: appConfig } = useQuery({
    queryKey: ["app-config"],
    queryFn: async () => {
      try {
        const response = await api.get('/app-config');
        return response.data;
      } catch (err) {
        console.log('App config not available, using defaults');
        return null;
      }
    },
    staleTime: 60000,
  });

  // Fetch module visibility settings
  const { data: moduleVisibility } = useQuery({
    queryKey: ['module-visibility'],
    queryFn: async () => {
      try {
        const response = await api.get('/module-visibility');
        return response.data;
      } catch {
        return null;
      }
    },
    staleTime: 60000 * 5
  });

  // Helper to check if events module is visible
  const isEventsVisible = moduleVisibility?.events?.visible !== false;
  const isEventsMaintenance = moduleVisibility?.events?.maintenance_mode === true;
  const eventsMaintenanceMsg = moduleVisibility?.events?.maintenance_message || 'Events section is under maintenance.';

  return (
    <PublicLayout>
      {/* Hero Section */}
      <HeroSection config={appConfig} />

      {/* Events Section - Respects visibility settings */}
      {isEventsVisible && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Upcoming Events</h2>
          
          {isEventsMaintenance ? (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-12 text-center">
                <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                <p className="text-slate-700 font-medium">{eventsMaintenanceMsg}</p>
              </CardContent>
            </Card>
          ) : eventsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : events?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">No events available yet.</p>
                <p className="text-sm text-slate-500 mt-2">Check back later or create one!</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Features Section */}
      <FeaturesSection config={appConfig} />

      {/* Testimonials Section */}
      <TestimonialsSection config={appConfig} />

      {/* Partners Section */}
      <PartnersSection config={appConfig} />

      {/* FAQ Section */}
      <FAQSection config={appConfig} />

      {/* Footer */}
      {appConfig?.footer_text && (
        <div className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">{appConfig.footer_text}</p>
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

export default LandingPage;
